import React, { useContext, useEffect } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { useStoreContext } from 'features/store';
import { DEFAULT_STORE_TIMEZONE } from 'features/store/constants';
import { getUSState } from 'features/store/utils';
import { Linking, StyleSheet } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { IAddress, IStoreDetails } from '@rbi-ctg/menu';
import { StoreSanityOrderEta } from 'components/sanity-store-order-eta/store-sanity-order-eta';
import StaticMap from 'components/static-map';
import { Box } from 'components/ui';
import { ServiceMode } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import { SmartAlertsContext } from 'state/smart-alert';
import { layout, spacing } from 'styles/constants';

import {
  Address,
  BoldText,
  Directions,
  MapContainer,
  PickUpTimeContainer,
  PickUpWindowAlert,
  PickUpWindowAlertContainer,
  PressableStoreContainer,
} from './styled';
import { getMapUrl } from './utils';

interface IMapCard {
  storeAddress: IAddress;
  storeDetails: IStoreDetails;
  orderDate?: Date | null;
  serverOrderServiceMode?: ServiceMode;
}

const MapCard = ({
  storeAddress,
  storeDetails,
  orderDate = null,
  serverOrderServiceMode,
}: IMapCard) => {
  const { store } = useStoreContext();
  const { orderSmartAlert, setServerOrderServiceMode, setServerOrderStoreId } = useContext(
    SmartAlertsContext
  );

  // Store Address Info
  const city = storeAddress?.city ?? '';
  const state = getUSState(storeAddress.state ?? '');
  const postalCode = storeAddress.zip.split('-')[0];
  const directions = t('directions');
  const addressLine = storeAddress.addressLine1 ?? '';
  const addressAccessibilityLabel = `${addressLine}, ${city} ${
    state ? ` ${state}` : ''
  }, ${postalCode} ${directions}`;
  // Store coordinates
  const lat = storeDetails.latitude ?? 0;
  const lng = storeDetails.longitude ?? 0;

  let formattedOrderTime = '';
  let formattedOrderDate = '';
  const locale = t('localeDateTime');

  if (orderDate) {
    formattedOrderDate = orderDate.toLocaleString(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: store.timezone || DEFAULT_STORE_TIMEZONE,
    });
    formattedOrderTime = orderDate.toLocaleString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: store.timezone || DEFAULT_STORE_TIMEZONE,
    });
  }

  const openMapUrl = React.useCallback(() => {
    logEvent(CustomEventNames.STORE_MAP_GET_DIRECTIONS, EventTypes.Other);
    const url = getMapUrl({ coordinates: `${lat},${lng}`, label: addressLine });

    if (url) {
      Linking.openURL(url);
    }
  }, [addressLine, lat, lng]);

  useEffect(() => {
    setServerOrderServiceMode(serverOrderServiceMode ?? null);
    setServerOrderStoreId(serverOrderServiceMode ? storeDetails.storeNumber : null);

    return () => {
      setServerOrderServiceMode(null);
      setServerOrderStoreId(null);
    };
  }, [
    serverOrderServiceMode,
    setServerOrderServiceMode,
    setServerOrderStoreId,
    storeDetails.storeNumber,
  ]);

  return (
    <Box>
      <MapContainer>
        <StaticMap
          position={{
            lat,
            lng,
          }}
          enablePointerEvents
        />
      </MapContainer>
      <PressableStoreContainer
        accessibilityRole="button"
        accessibilityHint={t('viewOnMap')}
        accessibilityLabel={addressAccessibilityLabel}
        onPress={openMapUrl}
        borderBottomRadius={0}
      >
        <Box style={styles.storeDetailsContainer}>
          <BoldText>{addressLine}</BoldText>
          <Address>{`${city}${state ? ` ${state}` : ''}, ${postalCode}`}</Address>
          <Box style={styles.directionsRow}>
            <Directions>{directions}</Directions>
            <Box style={styles.storeEtaContainer}>
              <StoreSanityOrderEta store={store} />
            </Box>
          </Box>
        </Box>
      </PressableStoreContainer>
      {orderDate && (
        <>
          <PickUpTimeContainer showBorderRadius={!orderSmartAlert}>
            <Typography
              variant="copyOne"
              data-testid="pickup-time-header"
              style={{ textTransform: 'capitalize' }}
            >
              {t('pickUpTime')}
            </Typography>
            <BoldText mt="$1" data-testid="pickup-time-value">
              {formattedOrderTime}
            </BoldText>
            <Typography variant="copyOne" mt="$1">
              {formattedOrderDate}
            </Typography>
          </PickUpTimeContainer>
          {!!orderSmartAlert && (
            <PickUpWindowAlertContainer>
              <Icon variant="warning" size="xs" />
              <PickUpWindowAlert>{orderSmartAlert.message}</PickUpWindowAlert>
            </PickUpWindowAlertContainer>
          )}
        </>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  directionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  storeDetailsContainer: {
    width: layout.full,
  },
  storeEtaContainer: {
    maxWidth: spacing.$150,
  },
});

export default MapCard;
